import { buildLocalization } from '@/util'
import { buildPriceFormatter, IMaterialPriceData } from '@/types/material'
import { useSparePartsStore } from '@/store/spareParts'
import { DbConstants } from '@/const'
import { IShoppingCartItemDTO } from './IShoppingCartItemDTO'

/** Result of computing a single cart line's price from material data */
export interface ICartItemPriceResult {
    /** Computed unit price (material.price / material.priceQty) */
    unitPrice: number,
    /** Line amount (unitPrice × count) */
    amount: number,
    /** Whether this material's price is "on request" */
    hasPriceOnRequest: boolean
}

/**
 * Composable that provides reactive cart price calculation functions.
 *
 * Resolves material prices from the spare parts store and formats totals
 * using the current user currency. All returned functions are reactive —
 * results update automatically when currency, language, or spare part data changes.
 */
export function useCartPriceCalculator() {
    const sparePartStore = useSparePartsStore()
    const { priceFormatter } = buildPriceFormatter()
    const { localize } = buildLocalization()

    /**
     * Computes the unit price, line amount, and "price on request" flag for a cart item
     * by looking up the material in the spare parts store.
     */
    function computeItemPrice(item: IShoppingCartItemDTO): ICartItemPriceResult {
        const bomHeader = sparePartStore.sparePartMap?.get(item.materialNumber.toString())
        const material = bomHeader?.material

        if (!material) return { unitPrice: 0, amount: 0, hasPriceOnRequest: false }

        const formatter = priceFormatter.value
        const hasPriceOnRequest = formatter(material).hasPriceOnRequest()

        const unitPrice = (material.priceQty ?? 0) === 0
            ? 0
            : (material.price ?? 0) / (material.priceQty ?? 1)

        return { unitPrice, amount: item.count * unitPrice, hasPriceOnRequest }
    }

    /**
     * Computes a formatted total price text for a list of cart items,
     * e.g. "1.234,00 DKK" or "1.234,00 DKK + prices on request".
     */
    function computeTotalPriceText(items: IShoppingCartItemDTO[]): string {
        let totalAmount = 0
        let anyPriceOnRequest = false

        for (const item of items) {
            const result = computeItemPrice(item)
            totalAmount += result.amount
            if (result.hasPriceOnRequest) anyPriceOnRequest = true
        }

        const priceData = {
            isSparePart: true,
            price: totalAmount,
            priceQty: 1,
            sparePartStatus: DbConstants.sparePartStatusNames.activeInStock
        } as IMaterialPriceData

        let totalText = priceFormatter.value(priceData).formattedPrice()

        if (anyPriceOnRequest && totalAmount > 0) {
            totalText += ' + ' + localize('pricesOnRequest')
        }

        return totalText
    }

    return { computeItemPrice, computeTotalPriceText }
}
